import { useState, useRef, useEffect, useCallback } from 'react'

/*
  Scroll Spy replication from HTML wpresidence_scroll_spy widget.
  
  Layout:
  - Left column (35%, sticky): vertical timeline with dots + items (title + description)
  - Right column (flex 1): stacked images, one per item, only active one visible
  
  Behavior:
  - As user scrolls, the image sections pass through the viewport
  - IntersectionObserver detects which image section is most visible → updates active text item
  - Clicking a text item scrolls to its corresponding image
*/

const LOCATION_IMAGES = [
  '/assets/sell/downtown.webp',
  '/assets/sell/palm_jumeirah.jpg',
  '/assets/sell/Marina.jpg',
  '/assets/sell/dubai_hills.jpeg',
  '/assets/sell/arabian_ranches.webp',
]

// Static fallback (English) — used if no prop is passed
const LOCATIONS_STATIC = [
  {
    id: 'item-0',
    name: 'Downtown',
    image: '/assets/sell/downtown.webp',
    desc: [
      "Rising at the heart of Dubai's most iconic district, Downtown embodies the essence of refined urban living.",
      "Within, residents are immersed in an elevated lifestyle experience with panoramic views of the city.",
    ],
  },
  {
    id: 'item-1',
    name: 'Palm Jumeirah',
    image: '/assets/sell/palm_jumeirah.jpg',
    desc: [
      "Rooted in heritage yet shaped by modern elegance, Jumeirah represents one of Dubai's most distinguished destinations.",
      "Living in Jumeirah is defined by balance and prestige.",
    ],
  },
  {
    id: 'item-2',
    name: 'Marina',
    image: '/assets/sell/Marina.jpg',
    desc: [
      "Rising along Dubai's shimmering coastline, Dubai Marina captures the energy of city living.",
      "Life in Dubai Marina is defined by movement and leisure.",
    ],
  },
  {
    id: 'item-3',
    name: 'Dubai Hills',
    image: '/assets/sell/dubai_hills.jpeg',
    desc: [
      "Nestled within the heart of Dubai, Dubai Hills redefines modern living.",
      "Life in Dubai Hills is defined by wellness, elegance, and community.",
    ],
  },
  {
    id: 'item-4',
    name: 'Arabian Ranches',
    image: '/assets/sell/arabian_ranches.webp',
    desc: [
      "Set amidst sprawling landscapes and tranquil greenery, Arabian Ranches offers suburban serenity.",
      "Life in Arabian Ranches is defined by family, leisure, and refinement.",
    ],
  },
]

const NAVY = '#002E53'

function LocationScroller({ locations: locProp, seeListings }) {
  const locations = (locProp || LOCATIONS_STATIC).map((loc, i) => ({
    ...loc,
    id: `item-${i}`,
    image: LOCATION_IMAGES[i],
  }))
  const [active, setActive] = useState(0)
  const imageRefs = useRef([])
  const scrollContainerRef = useRef(null)
  const isScrollingRef = useRef(false)

  // IntersectionObserver to detect which image section is in view
  useEffect(() => {
    const observers = []
    const options = {
      root: null,
      rootMargin: '-30% 0px -30% 0px',
      threshold: 0.1,
    }

    imageRefs.current.forEach((el, i) => {
      if (!el) return
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isScrollingRef.current) {
            setActive(i)
          }
        })
      }, options)
      observer.observe(el)
      observers.push(observer)
    })

    return () => observers.forEach((o) => o.disconnect())
  }, [])

  // Click on text item → scroll to corresponding image
  const handleItemClick = useCallback((index) => {
    setActive(index)
    const el = imageRefs.current[index]
    if (!el) return

    isScrollingRef.current = true
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setTimeout(() => { isScrollingRef.current = false }, 800)
  }, [])

  return (
    <div style={{ backgroundColor: '#FFFFFF', width: '100%' }}>
      <style>{`
        .scroll-spy-item h3 a { text-decoration: none; color: ${NAVY}; }
        .scroll-spy-item h3 a:hover { color: #3C5E6E; }
        .scroll-spy-cta { text-decoration: none; color: ${NAVY}; font-weight: 500; }
        .scroll-spy-cta:hover { text-decoration: underline; }
        @keyframes blue-pulse {
          0% { box-shadow: 0 0 0 0 rgba(0,46,83,0.35); }
          70% { box-shadow: 0 0 0 6px rgba(0,46,83,0); }
          100% { box-shadow: 0 0 0 0 rgba(0,46,83,0); }
        }
        .loc-desktop { display: flex; }
        .loc-mobile  { display: none; }
        @media (max-width: 768px) {
          .loc-desktop { display: none !important; }
          .loc-mobile  { display: block !important; }
        }
      `}</style>

      {/* ─── DESKTOP: scroll-spy side-by-side layout ─── */}
      <div
        ref={scrollContainerRef}
        className="loc-desktop"
        style={{
          maxWidth: '1300px',
          margin: '0 auto',
          padding: '30px 20px 40px 20px',
          boxSizing: 'border-box',
          flexDirection: 'row',
          alignItems: 'flex-start',
          minHeight: '100%',
        }}
      >
        {/* LEFT COLUMN — sticky text + timeline */}
        <div style={{
          flexBasis: '35%',
          flexShrink: 0,
          position: 'sticky',
          top: '80px',
          alignSelf: 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}>
          <div style={{
            position: 'relative',
            paddingLeft: '50px',
            marginLeft: '-10px',
          }}>
            {/* Vertical timeline line */}
            <div style={{
              position: 'absolute',
              left: '19px',
              top: 0,
              bottom: 0,
              width: '2px',
              backgroundColor: 'rgba(0,46,83,0.12)',
              zIndex: 0,
            }} />

            {locations.map((loc, i) => {
              const isActive = i === active
              return (
                <div
                  key={loc.id}
                  className="scroll-spy-item"
                  style={{ position: 'relative', padding: '0 0 7px 0', cursor: 'pointer' }}
                  onClick={() => handleItemClick(i)}
                >
                  <div style={{
                    position: 'absolute', left: '-41px', top: '6px',
                    width: '20px', height: '20px', borderRadius: '50%',
                    border: `2px solid ${NAVY}`,
                    backgroundColor: isActive ? NAVY : '#ffffff',
                    boxSizing: 'border-box', zIndex: 4,
                    transition: 'background-color 0.3s, box-shadow 0.3s',
                    animation: isActive ? 'blue-pulse 2s ease infinite' : 'none',
                  }} />
                  <h3 style={{
                    fontFamily: "'Poppins', sans-serif", fontSize: '26px',
                    fontWeight: 500, lineHeight: '25px', color: NAVY,
                    margin: '0 0 20px 0', padding: 0,
                  }}>
                    <a href={`#${loc.id}`} onClick={(e) => e.preventDefault()}>{loc.name}</a>
                  </h3>
                  <div style={{ display: isActive ? 'block' : 'none', marginTop: '-11px', padding: '0 0 10px 0' }}>
                    {loc.desc.map((para, j) => (
                      <p key={j} style={{
                        fontFamily: "'Poppins', sans-serif", fontSize: '16px',
                        fontWeight: 400, lineHeight: '1.6em', color: NAVY, margin: '0 0 12px 0',
                      }}>{para}</p>
                    ))}
                    {loc.cta && (
                      <p style={{ margin: '8px 0 0 0' }}>
                        <a className="scroll-spy-cta" href={loc.cta.href}>{loc.cta.label}</a>
                      </p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* RIGHT COLUMN — scrolling images */}
        <div style={{ flex: '1 1 0%', marginLeft: '85px', minHeight: '100%' }}>
          {locations.map((loc, i) => (
            <div
              key={loc.id}
              ref={(el) => { imageRefs.current[i] = el }}
              style={{
                minHeight: 'calc(100vh - 80px)',
                display: 'flex', flexDirection: 'column',
                justifyContent: 'center', padding: '20px 0',
              }}
            >
              <div style={{
                position: 'relative', maxWidth: '760px',
                borderRadius: '10px', overflow: 'hidden',
                boxShadow: '0px 4px 40px 0px rgba(0,0,0,0.1)',
              }}>
                <img src={loc.image} alt={loc.name} loading="lazy" decoding="async"
                  style={{ display: 'block', width: '100%', height: 'auto', margin: 0 }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── MOBILE: stacked card layout ─── */}
      <div className="loc-mobile" style={{ padding: '24px 16px 40px', boxSizing: 'border-box', maxWidth: '100%' }}>
        {locations.map((loc, i) => (
          <div key={loc.id} style={{
            marginBottom: '32px',
            background: '#fff',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,46,83,0.08)',
            border: '1px solid rgba(0,46,83,0.07)',
          }}>
            {/* Image */}
            <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', overflow: 'hidden' }}>
              <img
                src={loc.image} alt={loc.name} loading="lazy" decoding="async"
                style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            {/* Text */}
            <div style={{ padding: '20px 18px' }}>
              <h3 style={{
                fontFamily: "'Poppins', sans-serif", fontSize: '20px',
                fontWeight: 700, color: NAVY, margin: '0 0 10px',
              }}>{loc.name}</h3>
              {loc.desc.map((para, j) => (
                <p key={j} style={{
                  fontFamily: "'Poppins', sans-serif", fontSize: '14px',
                  fontWeight: 400, lineHeight: '1.6em', color: '#5a6577', margin: '0 0 8px',
                }}>{para}</p>
              ))}
              {loc.cta && (
                <a href={loc.cta.href} style={{
                  display: 'inline-block', marginTop: '10px',
                  color: NAVY, fontFamily: "'Poppins', sans-serif",
                  fontSize: '13px', fontWeight: 600, textDecoration: 'underline',
                }}>{loc.cta.label}</a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default LocationScroller
